import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';

/**
 * Thrown when a callable was about to be issued with no usable Firebase
 * identity. Callers catch this to say "your session was not ready yet, try
 * again" instead of relaying the server's `Must be logged in`, which reads as
 * "you are signed out" to someone who is demonstrably signed in.
 */
export class AuthNotReadyError extends Error {
  constructor(message = 'Firebase Auth is not ready: no signed-in user') {
    super(message);
    this.name = 'AuthNotReadyError';
  }
}

/**
 * Guarantees that a Firebase identity — and a **minted ID token** — exists
 * before a callable goes out.
 *
 * Why this exists (#274): the onboarding consent step failed with
 * `Must be logged in`, the message `requireUserId()` raises when
 * `request.auth` is empty (`backend/functions/src/shared/callableAuth.ts`),
 * on a user whose Firebase session was valid in local storage. The callables
 * are fired straight from component code — nothing anywhere waited for Auth
 * to have resolved, so a call issued during the boot window left without an
 * Authorization header, and the server saw an anonymous request.
 *
 * `authStateReady()` resolves once the persisted state has been restored, and
 * `getIdToken()` forces the token to exist rather than assuming the SDK has
 * one. The short poll after that covers the sign-up window, where
 * `currentUser` is populated a beat after the initial state resolves. Bounded
 * on purpose, like `WorkspaceService.waitForWorkspace()`: an unbounded wait
 * inside a submit handler is a spinner that never ends.
 */
@Injectable({ providedIn: 'root' })
export class AuthReadyService {
  private readonly auth = inject(Auth);

  /** Poll step while waiting for `currentUser`, in ms. */
  private static readonly POLL_MS = 100;

  /**
   * Resolves with the signed-in uid once a token can be minted.
   *
   * @param timeoutMs how long to wait for a user after the initial auth state
   *   has resolved. Default 4s — the same budget as `waitForWorkspace()`.
   * @throws {AuthNotReadyError} when no user shows up within the budget.
   */
  async whenAuthReady(timeoutMs = 4000): Promise<string> {
    await this.auth.authStateReady();

    const startedAt = Date.now();
    while (!this.auth.currentUser) {
      if (Date.now() - startedAt > timeoutMs) {
        throw new AuthNotReadyError();
      }
      await new Promise((resolve) =>
        setTimeout(resolve, AuthReadyService.POLL_MS),
      );
    }

    const user = this.auth.currentUser;
    // Force the token to exist. Having a `currentUser` is not the same as the
    // SDK holding a token it can attach: this is the step that makes the
    // difference between a callable that authenticates and one that arrives
    // anonymous.
    await user.getIdToken();
    return user.uid;
  }
}
